import debounce from '@mui/utils/debounce';
import { useAddresses } from '@si/hooks';
import type { Address } from '@si/types';
import { useTranslate } from '@si/utils';
import { useState } from 'react';

import { Autocomplete } from '../Autocomplete/Autocomplete';

type Props = {
  label?: string;
  value: Address | null;
  onChange: (address: Address | null) => void;
  required?: boolean;
  helperText?: string;
  errorText?: string;
  overrideVisited?: boolean;
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '40rem' | '100%';
};

export function AddressSearch({
  label,
  value,
  onChange,
  required = false,
  helperText,
  errorText,
  overrideVisited = false,
  inputWidth = '100%',
}: Props) {
  const minimumCharacters = 3;
  const { t, translate } = useTranslate();
  const [searchValue, setSearchValue] = useState('');

  const { addresses, isLoading, error } = useAddresses({
    enabled: searchValue.length > minimumCharacters,
    search: searchValue,
  });

  return (
    <Autocomplete
      errorText={error?.message ?? errorText}
      filterOptions={() => addresses}
      getOptionLabel={(address) => address.label}
      helperText={helperText}
      label={label || translate(t.address)}
      onChange={onChange}
      onInputChange={debounce((value) => setSearchValue(value), 300)}
      inputWidth={inputWidth}
      options={addresses}
      overrideVisited={overrideVisited}
      required={required}
      value={value}
      popperHidden={searchValue.length <= minimumCharacters || isLoading}
    />
  );
}
